import React, {useEffect, useState} from 'react';
import {Utilisateur} from "../../../../../components/models";
import {IconSpinner} from "../../../../../components/icons";

type TwoFactorMethod = 'totp' | 'email';

type TwoFactorData = {
  twoFactorRequired: boolean;
  twoFactorMethod: TwoFactorMethod | null;
  twoFactorEmail: string | null;
};

interface TwoFactorSettingsProps {
  utilisateur: Utilisateur;
  showReset2FAConfirm: any;
  setShowReset2FAConfirm: any;
  handleReset2FA: any;
  isResetting2FA: any;
  onChange?: (data: TwoFactorData) => void;
}

const buildTwoFactorData = (state: {
  required: boolean;
  method: TwoFactorMethod;
  email: string;
}): TwoFactorData => ({
  twoFactorRequired: state.required,
  twoFactorMethod: state.required ? state.method : null,
  twoFactorEmail: state.required && state.method === 'email' ? state.email : null,
});

const TwoFactorSettings: React.FC<TwoFactorSettingsProps> = (
  {
    utilisateur,
    showReset2FAConfirm,
    setShowReset2FAConfirm,
    handleReset2FA,
    isResetting2FA,
    onChange,
  }) => {
  const [required, setRequired] = useState(utilisateur.twoFactorRequired);
  const [method, setMethod] = useState<TwoFactorMethod>(utilisateur.twoFactorMethod ?? 'totp');
  const [email, setEmail] = useState(utilisateur.twoFactorEmail || utilisateur.email);

  // Insersion des données de l'utilisateur dans les données par défaut si modification
  useEffect(() => {
    onChange?.(buildTwoFactorData({ required, method, email }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Seul endroit où la règle métier est définie
  const emit = (next: { required: boolean; method: TwoFactorMethod; email: string }) => {
    onChange?.({
      twoFactorRequired: next.required,
      twoFactorMethod: next.required ? next.method : null,
      twoFactorEmail: next.required && next.method === 'email' ? next.email : null,
    });
  };

  const handleRequiredChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.checked;
    setRequired(value);
    emit({ required: value, method, email });
  };

  const handleMethodChange = (value: TwoFactorMethod) => {
    setMethod(value);
    emit({ required, method: value, email });
  };

  const fallbackEmail = utilisateur.twoFactorEmail || utilisateur.email;

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (value.trim() !== '') {
      emit({ required, method, email: value });
    }
  };

  const handleEmailBlur = () => {
    if (email.trim() === '') {
      setEmail(fallbackEmail);
      emit({ required, method, email: fallbackEmail });
    }
  };

  return (
    <div className="security-2fa">
      {/* Checkbox principale */}
      <label className="security-2fa__master">
        <input
          type="checkbox"
          checked={required}
          onChange={handleRequiredChange}
        />
        <span>
          Exiger l'authentification à deux facteurs (2FA)
        </span>
      </label>

      {/* Bloc configuration */}
      {required && (
        <div className="security-2fa__block">
          <p className="security-2fa__hint">
            Méthode utilisée par cet utilisateur pour valider ses connexions.
          </p>
          {/* Choix de méthode */}
          <div className="security-2fa__options">
            {/* TOTP */}
            <label
              className={`flex justify-between security-2fa__option ${
                method === 'totp' ? 'is-selected' : ''
              }`}
            >
              <div className="security-2fa__option-head">
                <i className="ti ti-device-mobile" aria-hidden="true"/>
                <input
                  type="radio"
                  name="two_factor_method"
                  value="totp"
                  checked={method === 'totp'}
                  onChange={() => handleMethodChange('totp')}
                />
              </div>
              <div className="-mt-7">
                <p className="security-2fa__option-title">Authenticator</p>
                <p className="security-2fa__option-desc">
                  Code généré par une app (Google Authenticator, Authy...)
                </p>
              </div>
            </label>
            {/* Email */}
            <label
              className={`security-2fa__option ${
                method === 'email' ? 'is-selected' : ''
              }`}
            >
              <div className="security-2fa__option-head">
                <i className="ti ti-mail" aria-hidden="true"/>
                <input
                  type="radio"
                  name="two_factor_method"
                  value="email"
                  checked={method === 'email'}
                  onChange={() => handleMethodChange('email')}
                />
              </div>
              <div className="-mt-7">
                <p className="security-2fa__option-title">Email</p>
                <p className="security-2fa__option-desc">Code envoyé à une adresse email</p>
              </div>
            </label>
          </div>

          {/* Email uniquement si méthode = email */}
          {method === 'email' && (
            <div className="security-2fa__email-field">
              <label className="form-label">
                Adresse de réception du code
              </label>
              <input
                type="email"
                className="form-input"
                value={email}
                onChange={handleEmailChange}
                onBlur={handleEmailBlur}
              />
            </div>
          )}

          {/* Reset */}
          {(utilisateur?.twoFactorEnabled && method === "totp" && utilisateur?.twoFactorMethod === "totp") && (
            <div className="security-2fa__reset">
              <div>
                <p className="security-2fa__reset-title">Réinitialiser la méthode active</p>
                <p className="security-2fa__reset-desc">
                  L'utilisateur devra reconfigurer sa 2FA à la prochaine connexion.
                </p>
              </div>
              <div>
                {!showReset2FAConfirm ? (
                  <button
                    onClick={() => setShowReset2FAConfirm(true)}
                    className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-lg text-sm font-medium transition-colors"
                  >
                    Réinitialiser le 2FA
                  </button>
                ) : (
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-red-600 font-medium">Confirmer la réinitialisation ?</span>
                    <button
                      onClick={handleReset2FA}
                      disabled={isResetting2FA}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                    >
                      {isResetting2FA && <IconSpinner className="animate-spin w-4 h-4"/>}
                      Confirmer
                    </button>
                    <button
                      onClick={() => setShowReset2FAConfirm(false)}
                      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium"
                    >
                      Annuler
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      )}

    </div>
  );
};

export default TwoFactorSettings;